import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { convertDate } from '../../features/ConvertDate';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Loading from '../Loading/Loading';
import ShowMultiImageModal from '../Modal/ShowMultiImageModal';
import { showBranchInfoData } from '../../features/Branches/ShowInfo';

function ShowInfo() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const lang = i18n.language ? i18n.language.split('-')[0] : 'en';
  const isRTL = ['ar', 'ckb'].includes(lang);

  // modal state
  const [isOpenImages, setIsOpenImages] = useState(false);
  const [modalStartIndex, setModalStartIndex] = useState(0);

  useEffect(() => {
    if (id) dispatch(showBranchInfoData({ id }));
  }, [dispatch, id, i18n.language]);

  const { loading, error, data } = useSelector((state) => state.showBranchInfo || {});
  if (loading) return <Loading />;
  if (error) return <p className="text-center text-red-500">Error: {error}</p>;

  const fullUrl = (img) => (img ? `${process.env.REACT_APP_IMG_URL}${img}` : '');

  const handleImageClick = (img, index = 0) => {
    // open multi-image modal at clicked index
    setModalStartIndex(index);
    setIsOpenImages(true);
    // also set selectedImage for single-image modal if you use it elsewhere
    setSelectedImage(fullUrl(img));
  };

  const closeModal = () => {
    setIsOpenImages(false);
    setSelectedImage(null);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto p-6 space-y-6"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <motion.h1
          className="text-4xl font-extrabold text-gray-800"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {data?.title || 'No Title'}
        </motion.h1>

        <motion.p
          className="text-teal-700 text-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          {convertDate(data?.published_at)}
        </motion.p>

        <motion.div
          className="overflow-hidden rounded-xl cursor-pointer"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          onClick={() => handleImageClick(data?.image, 0)}
        >
          <img
            src={fullUrl(data?.image)}
            alt="Activity"
            style={{ aspectRatio: '16/9' }}
            className="rounded-xl object-cover hover:scale-105 transition-transform duration-500 w-full"
          />
        </motion.div>

        <motion.div
          className="text-gray-600 px-4 text-lg leading-relaxed break-words"
          dangerouslySetInnerHTML={{ __html: data?.description || '<p>No Description</p>' }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        />

        <motion.div className="grid grid-cols-2 sm:grid-cols-4 gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
          {data?.images?.map((image, index) => (
            <motion.img
              key={index}
              src={fullUrl(image)}
              alt={`Image ${index}`}
              className="w-full h-32 rounded-xl object-cover hover:scale-105 transition-transform duration-500 cursor-pointer"
              onClick={() => handleImageClick(image, index)}
            />
          ))}
        </motion.div>
        <div className="relative w-full max-w-4xl mx-auto overflow-hidden rounded-2xl border border-gray-200 shadow-lg bg-gray-100 transition-all duration-300 hover:shadow-2xl">
          <div className="relative pt-[56.25%]"> {/* 16:9 Aspect Ratio */}
            <iframe
              src={data?.link}
              className="absolute top-0 left-0 w-full h-full rounded-2xl"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>

      </motion.div>

      <ShowMultiImageModal
        images={data?.images || []}
        isOpen={isOpenImages}
        onClose={closeModal}
        startIndex={modalStartIndex}
        isRTL={isRTL}
      />
    </>
  );
}

export default ShowInfo;
